import {Client, IFrame} from '@stomp/stompjs';
import React, {createContext, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {LOCAL_STORAGE_ACCESS_TOKEN_KEY} from '../auth';
import {toWebSocketUrl} from "../../app/utils";

type PendingMsg = {
    destination: string;
    body: any;
    headers?: Record<string, string>;
};

type StompContextValue = {
    client: Client | null;
    connected: boolean;
    publish: (destination: string, body: any, headers?: Record<string, string>) => void;
};

const brokerURL = toWebSocketUrl(process.env.REACT_APP_BACKEND_URL!);

const StompClientContext = createContext<StompContextValue>({
    client: null,
    connected: false,
    publish: () => {
    },
});

export const StompClientProvider: React.FC<{ children: React.ReactNode }> = ({children}) => {
    const [connected, setConnected] = useState(false);
    const clientRef = useRef<Client | null>(null);
    const pendingRef = useRef<PendingMsg[]>([]);

    const flush = () => {
        const c = clientRef.current;
        if (!c || !c.connected) {
            console.warn("Can't flush - No client connected");
            return;
        }

        const q = pendingRef.current.splice(0, pendingRef.current.length);
        q.forEach(m => {
            c.publish({
                destination: m.destination,
                body: JSON.stringify(m.body),
                headers: m.headers,
            });
        });
    };

    const publish = (destination: string, body: any, headers?: Record<string, string>) => {
        const c = clientRef.current;

        if (!c || !c.connected) {
            console.log("Can't publish before connect - adding message to queue");
            pendingRef.current.push({destination, body, headers});
            return;
        }

        c.publish({destination, body: JSON.stringify(body), headers});
    };

    useEffect(() => {
        if (clientRef.current) return;

        const client = new Client({
            brokerURL,
            reconnectDelay: 3000,
            heartbeatIncoming: 10000,
            heartbeatOutgoing: 10000,
            debug: (s) => console.log('[STOMP]', s),

            beforeConnect: () => {
                const token = localStorage.getItem(LOCAL_STORAGE_ACCESS_TOKEN_KEY) || '';
                client.connectHeaders = token ? {Authorization: `Bearer ${token}`} : {};
            },

            onConnect: () => {
                console.log('[STOMP] GLOBAL CONNECTED');
                setConnected(true);
                flush();
            },

            onWebSocketClose: evt => {
                console.warn('[STOMP] websocket closed', evt);
                setConnected(false);
            },

            onDisconnect: () => {
                console.log('[STOMP] GLOBAL DISCONNECTED');
                setConnected(false);
            },

            onStompError: (frame: IFrame) => {
                console.error('stomp error', frame);
                // TODO token refresh
            },
        });

        client.activate();
        clientRef.current = client;

        return () => {
            pendingRef.current = [];
            client.deactivate().catch(() => {
            });
            clientRef.current = null;
            setConnected(false);
        };
    }, []);

    const value = useMemo(
        () => ({client: clientRef.current, connected, publish}),
        [connected]
    );

    return (
        <StompClientContext.Provider value={value}>
            {children}
        </StompClientContext.Provider>
    );
};

export const useStompClient = () => useContext(StompClientContext);
